import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listComments, createComment, deleteComment } from '../api/community';
import CommentItem from './CommentItem';

// Comments under a post. Loads on mount + after each mutation.
// Comment form shown only to authenticated users.

export default function CommentSection({ postId }) {
  const { user, getIdToken } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const data = await listComments(postId);
      setComments(data.comments || []);
    } catch (err) {
      setLoadError(err);
    }
    setLoading(false);
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true); setSubmitError('');
    try {
      const token = await getIdToken();
      if (!token) throw new Error('세션이 만료되었습니다');
      await createComment({ token, postId, body: body.trim() });
      setBody('');
      await load();
    } catch (err) {
      setSubmitError(err.message);
    }
    setSubmitting(false);
  };

  const remove = async (commentId) => {
    if (!window.confirm('댓글을 삭제할까요?')) return;
    try {
      const token = await getIdToken();
      if (!token) throw new Error('세션이 만료되었습니다');
      await deleteComment({ token, commentId });
      await load();
    } catch (err) {
      alert(`삭제 실패: ${err.message}`);
    }
  };

  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 16 }}>
        댓글 {comments.length > 0 && <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>({comments.length})</span>}
      </h2>

      {user && (
        <form onSubmit={submit} style={{ marginBottom: 24 }}>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="댓글을 입력하세요 (최대 1000자)"
            maxLength={1000}
            rows={3}
            style={{
              width: '100%', padding: '10px 14px',
              borderRadius: 'var(--radius)', border: '1px solid var(--border)',
              background: 'var(--bg)', color: 'var(--text)',
              fontSize: '0.95rem', resize: 'vertical', fontFamily: 'inherit',
              marginBottom: 8,
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {submitError ? (
              <span style={{ color: '#ff5252', fontSize: '0.85rem' }}>❌ {submitError}</span>
            ) : <span />}
            <button type="submit" className="btn btn-primary"
              disabled={submitting || !body.trim()}
              style={{ padding: '8px 18px', fontSize: '0.9rem' }}>
              {submitting ? '작성 중...' : '댓글 작성'}
            </button>
          </div>
        </form>
      )}

      {!user && (
        <p style={{
          padding: 14, marginBottom: 24,
          background: 'var(--surface)', borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center',
        }}>
          댓글을 작성하려면{' '}
          <Link
            to={`/login?redirect=${encodeURIComponent(`/community/post/${postId}`)}`}
            style={{ color: 'var(--primary)', textDecoration: 'underline' }}
          >
            로그인
          </Link>
          이 필요합니다.
        </p>
      )}

      {loading && <p style={{ color: 'var(--text-muted)' }}>로딩 중...</p>}

      {loadError && (
        <p style={{ color: '#ff5252', fontSize: '0.85rem' }}>
          ❌ 댓글을 불러올 수 없습니다: {loadError.message}
        </p>
      )}

      {!loading && !loadError && comments.length === 0 && (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>
          아직 댓글이 없습니다. 첫 댓글을 남겨보세요.
        </p>
      )}

      {!loading && !loadError && comments.length > 0 && (
        <div>
          {comments.map(c => (
            <CommentItem key={c.commentId} comment={c} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}
